import {
  diffFields,
  type FieldSpec,
  type LogChange,
} from "@lift-admin/utils/logChangeFormatter";

/** Fields included in performance-account admin log diffs. */
export const PERFORMANCE_ACCOUNT_FIELD_SPECS: FieldSpec[] = [
  { key: "display_name", label: "Platform" },
  { key: "formal_name", label: "Formal Name" },
  { key: "account_id", label: "Account ID" },
  { key: "account_name", label: "Account Name" },
  { key: "property_id", label: "Property ID" },
  { key: "property_name", label: "Property Name" },
  // Mode: "Define Window" | "Use unified attribution" | free-text
  { key: "attribution_window", label: "Attribution Window" },
  // Only meaningful while mode is "Define Window" (stored as "click/view")
  { key: "click_window", label: "Click Window" },
  { key: "view_window", label: "View Window" },
  { key: "attribution_basis", label: "Attribution Basis" },
  { key: "selected_conversion_event", label: "Selected Conversion Event" },
  {
    key: "selected_conversion_tag_names",
    label: "Conversion Tag Names",
    type: "list",
  },
  { key: "currency_conversion", label: "Currency Conversion" },
  { key: "backfill_status", label: "Backfill Status" },
  {
    key: "is_mbm",
    label: "Metaplier Access",
    type: "boolean",
    boolStyle: "yesNo",
  },
  {
    key: "is_emq",
    label: "Track EMQ",
    type: "boolean",
    boolStyle: "yesNo",
  },
  { key: "emq_primary_conversion", label: "Primary Conversion" },
  { key: "emq_secondary_conversion", label: "Secondary Conversion" },
  { key: "emq_tertiary_conversion", label: "Tertiary Conversion" },
  {
    key: "source_of_truth",
    label: "Primary",
    type: "boolean",
    boolStyle: "yesNo",
  },
  {
    key: "is_automation_enabled",
    label: "Automation Enabled",
    type: "boolean",
    boolStyle: "yesNo",
  },
  {
    key: "is_active",
    label: "Status",
    type: "boolean",
    boolStyle: "activeInactive",
  },
  {
    key: "keyword_level_needed",
    label: "Keyword Level Templates Needed",
    type: "boolean",
    boolStyle: "yesNo",
  },
  { key: "is_enterprise_client", label: "UTM Level" },
  { key: "notes", label: "Notes" },
];

const DEFINE_WINDOW = "Define Window";
const UNIFIED_ATTRIBUTION = "Use unified attribution";
const WINDOW_FIELD_LABELS = new Set(["Click Window", "View Window"]);

const toBool = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "yes" || v === "true" || v === "1";
  }
  return Boolean(value);
};

const toTagList = (value: any): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Format day count for attribution window logs (e.g. 1 → "1 day", 7 → "7 days"). */
const formatWindowDays = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (!/^\d+$/.test(trimmed)) return trimmed;
  const n = Number(trimmed);
  return n === 1 ? "1 day" : `${n} days`;
};

/**
 * Split stored attribution values into mode + Click/View windows.
 * "1/7" → mode "Define Window" + click/view days
 * "DEFAULT" → "Use unified attribution"
 */
function splitAttributionWindow(value: any): {
  attribution_window: string;
  click_window: string;
  view_window: string;
} {
  if (value == null || value === "") {
    return { attribution_window: "", click_window: "", view_window: "" };
  }

  const raw = String(value).trim();

  if (raw.includes("/")) {
    const [click = "", view = ""] = raw.split("/");
    return {
      attribution_window: DEFINE_WINDOW,
      click_window: formatWindowDays(click),
      view_window: formatWindowDays(view),
    };
  }

  if (raw.toUpperCase() === "DEFAULT" || raw === UNIFIED_ATTRIBUTION) {
    return {
      attribution_window: UNIFIED_ATTRIBUTION,
      click_window: "",
      view_window: "",
    };
  }

  // Already a mode label or free-text platform value
  if (raw === DEFINE_WINDOW || raw.toLowerCase() === "define window") {
    return {
      attribution_window: DEFINE_WINDOW,
      click_window: "",
      view_window: "",
    };
  }

  return { attribution_window: raw, click_window: "", view_window: "" };
}

/** Normalize form/API account shapes into comparable log fields. */
export function normalizePerformanceAccountForLog(
  item: any
): Record<string, any> {
  if (!item) return {};
  const attributionParts = splitAttributionWindow(item.attribution_window);
  return {
    display_name: item.display_name ?? "",
    formal_name: item.formal_name ?? "",
    account_id: item.account_id ?? "",
    account_name: item.account_name ?? "",
    property_id: item.property_id ?? "",
    property_name: item.property_name ?? "",
    ...attributionParts,
    attribution_basis: item.attribution_basis ?? "",
    selected_conversion_event: item.selected_conversion_event ?? "",
    selected_conversion_tag_names: toTagList(
      item.selected_conversion_tag_names
    ),
    currency_conversion: item.currency_conversion ?? "",
    backfill_status: item.backfill_status ?? "",
    is_mbm: toBool(item.is_mbm),
    is_emq: toBool(item.is_emq),
    emq_primary_conversion: item.emq_primary_conversion ?? "",
    emq_secondary_conversion: item.emq_secondary_conversion ?? "",
    emq_tertiary_conversion: item.emq_tertiary_conversion ?? "",
    source_of_truth: toBool(item.source_of_truth),
    is_automation_enabled: toBool(item.is_automation_enabled),
    is_active: toBool(item.is_active),
    keyword_level_needed: toBool(item.keyword_level_needed),
    is_enterprise_client: item.is_enterprise_client ?? "",
    notes: item.notes ?? "",
  };
}

/**
 * Diff performance accounts for admin logs.
 * When leaving Define Window → unified, omit Click/View "cleared to Not Set" noise.
 * When entering Define Window (or changing within it), keep Click/View rows.
 */
export function diffPerformanceAccountForLog(
  previous: any,
  next: any
): LogChange[] {
  const previousNormalized = normalizePerformanceAccountForLog(previous);
  const nextNormalized = normalizePerformanceAccountForLog(next);
  const changes = diffFields(
    previousNormalized,
    nextNormalized,
    PERFORMANCE_ACCOUNT_FIELD_SPECS
  );

  const leavingDefineWindow =
    previousNormalized.attribution_window === DEFINE_WINDOW &&
    nextNormalized.attribution_window !== DEFINE_WINDOW;

  if (!leavingDefineWindow) return changes;

  return changes.filter((change) => !WINDOW_FIELD_LABELS.has(change.label));
}
